import React, { useState, ReactNode, useEffect } from 'react';
import cn from 'classnames';
import Modal, { ModalButton } from '@components/Modals';
import Contact from '@models/Contact';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchArchivedContact,
  restoreArchivedContact,
  selectArchivedContact,
} from '@actions/archive';
import { LoaderIcon } from '@components/Icons';
import styles from './Sources.module.scss';

const ContactConflictCopy = ({ contact, source }: { contact: Contact; source: string }) => (
  <>
    <p>In Clay, sources for all of your People must be unique.</p>
    <p>
      In order to add "{source}" to {contact.displayName}'s Sources, you can either:
    </p>
    <ul>
      <li>
        Remove "{source}" from the conflicting Person's Sources and add it to {contact.displayName},
        or
      </li>
      <li>Merge the conflicting Person with {contact.displayName}</li>
    </ul>
  </>
);

const ArchivedContactConflictCopy = ({ contact, source }: { contact: Contact; source: string }) => (
  <>
    <p>In Clay, sources for all of your People must be unique.</p>
    <p>
      Before "{source}" can be added to {contact.displayName}'s Sources, you will need to restore
      the Archived Person who already has this source. After restoring, you can either:
    </p>
    <ul>
      <li>
        Remove "{source}" from the Restored Person's Sources and add it to {contact.displayName}, or
      </li>
      <li>Merge the Restored Person with {contact.displayName}</li>
    </ul>
  </>
);

export default function SourceConflictModal({
  contact,
  error,
  closeModal,
}: {
  contact: Contact;
  error: any;
  closeModal: () => void;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const archivedContact = useSelector(selectArchivedContact(error.response.contact_id));
  const [restoringConflictingContact, setRestoringConflictingContact] = useState(false);
  const source = error.requestValue;

  let title = `Another Person already has "${source}"`;
  let Copy = ContactConflictCopy;
  let buttonCopy: ReactNode = 'Go to other Person';
  let onButtonClick = () => navigate(`/contact/${error.response.contact_id}`);

  if (error.response.archived) {
    title = `An Archived Person already has "${source}"`;
    Copy = ArchivedContactConflictCopy;
    buttonCopy = restoringConflictingContact ? (
      <>
        Restoring, one moment <LoaderIcon />
      </>
    ) : (
      'Restore and go to other Person'
    );
    onButtonClick = () => {
      dispatch(restoreArchivedContact(error.response.contact_id));
      setRestoringConflictingContact(true);
    };
  }

  useEffect(() => {
    if (error.response.archived) {
      dispatch(fetchArchivedContact(error.response.contact_id));
    }
  }, []);

  useEffect(() => {
    if (!archivedContact && restoringConflictingContact) {
      closeModal();
      navigate(`/contact/${error.response.contact_id}`);
    }
  }, [archivedContact, navigate]);

  return (
    <Modal isOpen={true} onRequestClose={closeModal}>
      <div className="modal-header">{title}</div>
      <div className={cn(styles.modalContent, 'modal-content-padded')}>
        <Copy contact={contact} source={source} />
        <div className="modal-button-row">
          <ModalButton variant="muted" onClick={closeModal} tabIndex={0}>
            Cancel
          </ModalButton>
          <ModalButton variant="primary" onClick={onButtonClick} autoFocus tabIndex={0}>
            {buttonCopy}
          </ModalButton>
        </div>
      </div>
    </Modal>
  );
}
